import { createColumnHelper } from '@tanstack/react-table';
import { Badge } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { formatDateToDateTimeDisplay } from '@/utils/formatDateToDateTimeDisplay';
import { CustomTable } from '@/components/CustomTable/CustomTable';
import { Notification, NotificationDispatchStatus } from '@/model/notification-push';

const STATUS_COLOR_SCHEME: Record<NotificationDispatchStatus, string> = {
  [NotificationDispatchStatus.PENDING]: 'yellow',
  [NotificationDispatchStatus.PROCESSING]: 'blue',
  [NotificationDispatchStatus.SENT]: 'green',
  [NotificationDispatchStatus.FAILED]: 'red',
};

const STATUS_LABEL_KEY: Record<NotificationDispatchStatus, string> = {
  [NotificationDispatchStatus.PENDING]: 'status_pending',
  [NotificationDispatchStatus.PROCESSING]: 'status_processing',
  [NotificationDispatchStatus.SENT]: 'status_sent',
  [NotificationDispatchStatus.FAILED]: 'status_failed',
};

export interface NotificationsTabTableProps {
  data: Notification[];
  isLoading: boolean;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  totalItems: number;
  sorting?: { field: string; desc: boolean };
  onSortingChange?: (field: string, desc: boolean) => void;
  onPaginationChange: (page: number, pageSize: number) => void;
}

export const NotificationsTabTable = ({
  data,
  isLoading,
  pageNumber,
  pageSize,
  totalPages,
  sorting,
  totalItems,
  onSortingChange,
  onPaginationChange,
}: NotificationsTabTableProps) => {
  const { t } = useTranslation();

  const columnHelper = createColumnHelper<{
    uuid: string;
    created_at: string;
    updated_at: string;
    title: string;
    content: string;
    status?: NotificationDispatchStatus;
  }>();

  const columns = [
    columnHelper.accessor('created_at', {
      cell: props => formatDateToDateTimeDisplay(props.getValue()),
      header: 'DATA DA CRIAÇÃO',
    }),
    columnHelper.accessor('title', {
      cell: props => props.getValue(),
      header: 'NOME',
    }),
    columnHelper.accessor('content', {
      cell: props => props.getValue(),
      header: 'DESCRIÇÃO',
    }),
    columnHelper.accessor('status', {
      cell: props => {
        const status = props.getValue();
        if (!status || !STATUS_LABEL_KEY[status]) return null;

        return (
          <Badge colorScheme={STATUS_COLOR_SCHEME[status]} borderRadius='full' px={2} py='2px' textTransform='none' fontWeight='medium'>
            {t(`pages.communication_central.components.notifications_tab_table.${STATUS_LABEL_KEY[status]}`)}
          </Badge>
        );
      },
      header: t('pages.communication_central.components.notifications_tab_table.column_status'),
    }),
  ];

  return (
    <CustomTable
      data={data}
      isLoading={isLoading}
      pageNumber={pageNumber}
      pageSize={pageSize}
      totalPages={totalPages}
      totalItems={totalItems}
      columns={columns}
      onPaginationChange={onPaginationChange}
      sorting={sorting}
      onSortingChange={onSortingChange}
    />
  );
};
